import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { GeneratePersonasDialog } from '../lines-of-business/GeneratePersonasDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { useTenant } from '../../core/contexts/TenantContext';
import { useLineOfBiz } from '../../core/contexts/LineOfBizContext';
import type { LineOfBizPersona, Persona } from '../../core/types/data-models';

/** Form payload — only user-supplied fields, not the calculated aggregates. */
interface PersonaFormValues {
  id?: number;
  name: string;
  description: string | null;
  commonPersonaId: number | null;
  promptsWeight: number;
}

const EMPTY_FORM: PersonaFormValues = {
  name: '',
  description: null,
  commonPersonaId: null,
  promptsWeight: 0,
};

/**
 * Percentage of the LOB's prompts that fall under one persona. Returns
 * a whole number; guards against a zero denominator (no prompts yet).
 */
function promptShare(count: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((count / total) * 100);
}

/**
 * Personas page rendered as a list of expandable cards. Mirrors the
 * Categories page: each card's header shows the persona name, an
 * aggregate chip, and edit/delete affordances; expanding the card
 * reveals description, the linked global persona, and the usage count.
 *
 * Delete is gated by the aggregate usage counts — if the persona is
 * referenced by any report-template prompt/set it goes through an
 * informational dialog refusing the action; otherwise the user gets a
 * destructive confirm dialog.
 */
export function LineOfBizPersonasPage() {
  const { activeTenantId } = useTenant();
  const { activeLineOfBizId } = useLineOfBiz();
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['line-of-biz-personas', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources.lineOfBizPersonas.list({
        pageSize: 200,
        sort: 'name',
        filter: { lineOfBizId: activeLineOfBizId as number },
      }),
    enabled: activeLineOfBizId != null,
  });

  // Total number of prompts in this LOB — the denominator for each
  // persona's "share of prompts" figure. We only need the count, so we
  // ask for a single row and read `page.total` off the envelope.
  const promptsTotalQuery = useQuery({
    queryKey: ['prompts', 'count', { lineOfBizId: activeLineOfBizId }],
    queryFn: () =>
      resources.prompts.list({
        page: 1,
        pageSize: 1,
        filter: { lineOfBizId: activeLineOfBizId as number },
      }),
    enabled: activeLineOfBizId != null,
  });
  const totalLobPrompts = promptsTotalQuery.data?.page.total ?? 0;

  // Shared catalog of common personas — used both for the card's
  // "Common persona" line and for the form's picker.
  const commonCatalog = useQuery({
    queryKey: ['personas', 'select'],
    queryFn: () => resources.personas.list({ pageSize: 500, sort: 'name' }),
  });
  const commonNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of commonCatalog.data?.data ?? []) map.set(p.id, p.name);
    return map;
  }, [commonCatalog.data]);

  const rows: LineOfBizPersona[] = list.data?.data ?? [];

  // Running total of the per-persona prompt weights. All personas under a
  // LOB are meant to sum to 100% — surface the current total so the user
  // can tell at a glance whether the budget is balanced.
  const totalWeight = useMemo(
    () => rows.reduce((sum, r) => sum + (r.promptsWeight ?? 0), 0),
    [rows],
  );

  // Expanded-state lives in a Set so expand-all / collapse-all can flip
  // every card with a single setState. An effect prunes stale ids so the
  // set tracks the row list across add / delete.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set<number>();
      for (const r of rows) if (prev.has(r.id)) next.add(r.id);
      return next;
    });
  }, [rows]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(rows.map((r) => r.id)));
  const collapseAll = () => setExpanded(new Set());
  const allExpanded = rows.length > 0 && expanded.size === rows.length;

  // Page-level mode toggle — view (browse, hides destructive +
  // create affordances) vs. edit (full controls). Defaults to view.
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  // ----- Create / edit dialog -----------------------------------------
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PersonaFormValues>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  };
  const openEdit = (row: LineOfBizPersona) => {
    setEditing({
      id: row.id,
      name: row.name,
      description: row.description ?? null,
      commonPersonaId: row.commonPersonaId,
      promptsWeight: row.promptsWeight ?? 0,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const create = useMutation({
    mutationFn: (data: PersonaFormValues) =>
      resources.lineOfBizPersonas.create({
        name: data.name,
        description: data.description,
        commonPersonaId: data.commonPersonaId,
        promptsWeight: data.promptsWeight,
        lineOfBizId: activeLineOfBizId as number,
        ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
      } as Partial<LineOfBizPersona>),
    onSuccess: () => {
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-personas'] });
    },
    onError: (err) => setFormError(extractErrorMessage(err)),
  });

  const update = useMutation({
    mutationFn: (data: PersonaFormValues & { id: number }) =>
      resources.lineOfBizPersonas.update(data.id, {
        name: data.name,
        description: data.description,
        commonPersonaId: data.commonPersonaId,
        promptsWeight: data.promptsWeight,
      } as Partial<LineOfBizPersona>),
    onSuccess: () => {
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-personas'] });
    },
    onError: (err) => setFormError(extractErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => resources.lineOfBizPersonas.remove(id),
    onSuccess: () => {
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-personas'] });
    },
  });

  // Two distinct delete states — see the Categories page for the
  // rationale. Same in-use rule: any non-zero aggregate count blocks.
  const [confirmDelete, setConfirmDelete] = useState<LineOfBizPersona | null>(null);
  const [blockedDelete, setBlockedDelete] = useState<LineOfBizPersona | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);

  const requestDelete = (row: LineOfBizPersona) => {
    const inUse =
      (row.reportTemplatePromptCount ?? 0) + (row.reportTemplatePromptSetCount ?? 0);
    if (inUse > 0) setBlockedDelete(row);
    else setConfirmDelete(row);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!editing.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (editing.id != null) {
      await update.mutateAsync({ ...editing, id: editing.id });
    } else {
      await create.mutateAsync(editing);
    }
  };

  if (activeLineOfBizId == null) {
    return (
      <Box>
        <PageHeader
          title="Personas"
          description="Personas scoped to a specific line of business; optionally derived from a global catalog entry."
        />
        <Alert severity="info">
          Select a line of business from the sidebar to view its personas.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Personas"
        description="Personas scoped to a specific line of business; optionally derived from a global catalog entry."
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
              <span>
                <IconButton
                  onClick={allExpanded ? collapseAll : expandAll}
                  disabled={rows.length === 0}
                  aria-label={allExpanded ? 'collapse all' : 'expand all'}
                >
                  {allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                </IconButton>
              </span>
            </Tooltip>
            {mode === 'edit' && (
              <>
                {/* Opens the GeneratePersonasDialog — kicks off the LLM
                    job (POST /line-of-biz-personas/generate-persona/:id),
                    shows progress, then lets the user pick which
                    suggested personas to write into this LOB. */}
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setGenerateOpen(true)}
                >
                  Generate personas
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                >
                  New
                </Button>
              </>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, next) => {
                if (next === 'view' || next === 'edit') setMode(next);
              }}
              aria-label="page mode"
            >
              <ToggleButton value="view" aria-label="view mode">
                <Tooltip title="View mode">
                  <VisibilityIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="edit" aria-label="edit mode">
                <Tooltip title="Edit mode">
                  <EditNoteIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
      />

      {list.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractErrorMessage(list.error)}
        </Alert>
      )}

      {/* Prompt-weight budget banner — green when the weights sum to
          exactly 100%, otherwise a warning showing the running total so
          the user knows how far off balance the personas are. */}
      {rows.length > 0 && (
        <Alert severity={totalWeight === 100 ? 'success' : 'warning'} sx={{ mb: 2 }}>
          Prompt weights total <strong>{totalWeight}%</strong>
          {totalWeight === 100
            ? ' — personas are balanced.'
            : ` — should sum to 100% (off by ${Math.abs(100 - totalWeight)}%).`}
        </Alert>
      )}

      {!list.isLoading && rows.length === 0 ? (
        <Alert severity="info">
          No personas yet. Click <strong>New</strong> to add one.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {rows.map((row) => {
            const isOpen = expanded.has(row.id);
            const commonName =
              row.commonPersonaId == null
                ? null
                : commonNameById.get(row.commonPersonaId) ?? `#${row.commonPersonaId}`;
            return (
              <Accordion
                key={row.id}
                expanded={isOpen}
                onChange={() => toggle(row.id)}
                disableGutters
                sx={{
                  '&:before': { display: 'none' },
                  borderRadius: 1,
                  transition: (t) => t.transitions.create(['box-shadow', 'border-color']),
                  '&:hover': { boxShadow: 2 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ flexGrow: 1, pr: 1 }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: 'primary.main' }}
                    >
                      {row.name}
                    </Typography>
                    {/* Row id — only visible in edit mode; empty spacer
                        keeps the chip + action buttons on the right. */}
                    {mode === 'edit' ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexGrow: 1, fontFamily: 'monospace' }}
                      >
                        #{row.id}
                      </Typography>
                    ) : (
                      <Box sx={{ flexGrow: 1 }} />
                    )}
                    <Tooltip title="Configured weight — target share of this LOB's prompt budget assigned to this persona">
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={`Weight ${row.promptsWeight ?? 0}%`}
                      />
                    </Tooltip>
                    <Tooltip title="Actual share — this persona's prompts as a percentage of the LOB's total prompts">
                      <Chip
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label={`${promptShare(row.promptCount, totalLobPrompts)}% of prompts`}
                      />
                    </Tooltip>
                    <Tooltip title="Prompts whose default persona is this one">
                      <Chip
                        size="small"
                        label={`${row.promptCount} prompt${row.promptCount === 1 ? '' : 's'}`}
                      />
                    </Tooltip>
                    {mode === 'edit' && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(row);
                            }}
                            aria-label="edit persona"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={remove.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDelete(row);
                            }}
                            aria-label="delete persona"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body2">
                        {row.description || <em style={{ opacity: 0.6 }}>No description</em>}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Common persona
                      </Typography>
                      <Typography variant="body2">
                        {commonName ?? <em style={{ opacity: 0.6 }}>None</em>}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Line-of-business prompts using this persona
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {row.promptCount}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      <FormDialog
        open={dialogOpen}
        title={editing.id != null ? 'Edit persona' : 'New persona'}
        submitLabel={editing.id != null ? 'Save' : 'Create'}
        loading={create.isPending || update.isPending}
        onClose={() => {
          if (create.isPending || update.isPending) return;
          setDialogOpen(false);
        }}
        onSubmit={handleSubmit}
      >
        {formError && <Alert severity="error">{formError}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Name"
            required
            value={editing.name}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
          <TextField
            label="Description"
            multiline
            minRows={2}
            value={editing.description ?? ''}
            onChange={(e) =>
              setEditing({
                ...editing,
                description: e.target.value === '' ? null : e.target.value,
              })
            }
          />
          <EntitySelect<Persona>
            cacheKey={['personas', 'select']}
            service={resources.personas}
            label="Common persona"
            helperText="Optional — link this LOB persona to a global catalog entry."
            value={editing.commonPersonaId}
            onChange={(id) => setEditing({ ...editing, commonPersonaId: id })}
          />
          {/* Prompt weight — percentage share of this LOB's prompt budget.
              All personas should sum to 100%; we don't enforce it here,
              the page banner surfaces the running total instead. */}
          <TextField
            label="Prompts weight (%)"
            type="number"
            value={editing.promptsWeight}
            onChange={(e) => {
              const n = Number(e.target.value);
              setEditing({
                ...editing,
                promptsWeight: Number.isFinite(n) ? n : 0,
              });
            }}
            inputProps={{ min: 0, max: 100, step: 1 }}
            helperText="Share of the LOB's total prompts. All personas should sum to 100%."
          />
        </Stack>
      </FormDialog>

      <GeneratePersonasDialog
        open={generateOpen}
        lineOfBizId={activeLineOfBizId}
        onClose={() => setGenerateOpen(false)}
      />

      {/* Delete-blocked informational dialog — fires when the persona is
          referenced by at least one prompt or prompt-set. */}
      <ConfirmDialog
        open={blockedDelete != null}
        title="Can't delete persona"
        message={
          blockedDelete
            ? `"${blockedDelete.name}" is used by ${
                (blockedDelete.reportTemplatePromptCount ?? 0)
                  + (blockedDelete.reportTemplatePromptSetCount ?? 0)
              } report-template prompt${
                ((blockedDelete.reportTemplatePromptCount ?? 0)
                  + (blockedDelete.reportTemplatePromptSetCount ?? 0)) === 1
                  ? ''
                  : 's'
              } / prompt sets. Reassign or remove those first, then try again.`
            : ''
        }
        confirmLabel="OK"
        cancelLabel="Close"
        onConfirm={() => setBlockedDelete(null)}
        onClose={() => setBlockedDelete(null)}
      />

      <ConfirmDialog
        open={confirmDelete != null}
        title="Delete persona?"
        message={
          confirmDelete ? `Delete "${confirmDelete.name}"? This can't be undone.` : ''
        }
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
        }}
        onClose={() => {
          if (remove.isPending) return;
          setConfirmDelete(null);
        }}
      />
    </Box>
  );
}
